import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, Wrench, FileText, Gauge, CheckCircle, Activity as ActivityIcon } from "lucide-react";
import { api } from "../api/client";
import { PageHeader } from "../components/Layout";
import { EmptyState, ErrorState } from "../components/EmptyState";
import { mapNetworkError, formatDate } from "../lib/fleet";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const secs = Math.max(0, Math.floor((now - then) / 1000));
    if (secs < 60) return "à l'instant";
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `Il y a ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Il y a ${hours} h`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `Il y a ${days} j`;
    return formatDate(iso);
  } catch {
    return "";
  }
}

const KIND_META = {
  vehicle_created: { label: "Véhicule ajouté", tint: "var(--mf-brand)", icon: Car },
  intervention_created: { label: "Intervention", tint: "var(--mf-ok-solid)", icon: Wrench },
  document_added: { label: "Document ajouté", tint: "#8b5cf6", icon: FileText },
  mileage_updated: { label: "Kilométrage mis à jour", tint: "var(--mf-upcoming-solid)", icon: Gauge },
  custom_maintenance_created: { label: "Entretien personnalisé", tint: "var(--mf-ok-solid)", icon: Wrench },
  vehicle_deleted: { label: "Véhicule supprimé", tint: "var(--mf-urgent-solid)", icon: Car },
};

export default function Activity() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async (before) => {
    try {
      if (before) setLoadingMore(true);
      const params = before ? `?limit=50&before=${encodeURIComponent(before)}` : "?limit=50";
      const { data } = await api.get(`/activity${params}`);
      setItems((prev) => before ? [...prev, ...data.items] : data.items);
      setHasMore(!!data.has_more);
      setErrorMsg(null);
      setLoaded(true);
    } catch (e) {
      setErrorMsg(mapNetworkError(e));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { load(null); }, [load]);

  const onLoadMore = () => {
    if (!items.length) return;
    const last = items[items.length - 1];
    load(last.timestamp);
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }} data-testid="activity-page">
      <PageHeader title="Activité" subtitle="Vos actions récentes sur la flotte" />
      <div style={{ padding: "0 20px 100px" }}>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} style={{ height: 64, background: "var(--mf-bg-muted)", borderRadius: 14 }} />
            ))}
          </div>
        ) : errorMsg && !loaded ? (
          <ErrorState message={errorMsg} onRetry={() => { setLoading(true); load(null); }} testID="activity-error" />
        ) : items.length === 0 ? (
          <EmptyState
            testID="activity-empty"
            icon={<ActivityIcon size={64} color="var(--mf-border)" />}
            title="Aucune activité pour le moment"
            subtitle="Vos ajouts de véhicules, interventions et mises à jour de kilométrage apparaîtront ici."
          />
        ) : (
          <>
            <div style={{
              background: "var(--mf-bg-card)", borderRadius: 18,
              boxShadow: "var(--mf-shadow)", overflow: "hidden",
            }}>
              {items.map((it, i) => {
                const meta = KIND_META[it.kind] || { label: it.kind, tint: "var(--mf-text-muted)", icon: CheckCircle };
                const Icon = meta.icon;
                const primary = it.title || meta.label;
                const secondary = it.vehicle_label || "";
                const extras = renderMeta(it);
                return (
                  <button
                    key={it.id}
                    onClick={() => it.vehicle_id && navigate(`/vehicles/${it.vehicle_id}`)}
                    data-testid={`activity-list-row-${i}`}
                    style={{
                      width: "100%", background: "transparent", border: "none",
                      textAlign: "left", padding: "14px 16px",
                      display: "flex", alignItems: "center", gap: 12,
                      cursor: it.vehicle_id ? "pointer" : "default",
                      borderTop: i === 0 ? "none" : "1px solid var(--mf-divider)",
                    }}
                  >
                    <span style={{
                      width: 36, height: 36, borderRadius: 12,
                      background: `${meta.tint}18`, color: meta.tint,
                      display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <Icon size={18} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--mf-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {primary}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--mf-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {secondary}{extras ? ` · ${extras}` : ""} · {timeAgo(it.timestamp)}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {hasMore && (
              <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
                <button
                  onClick={onLoadMore}
                  disabled={loadingMore}
                  data-testid="activity-load-more"
                  style={{
                    padding: "10px 20px", borderRadius: 999,
                    background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)",
                    color: "var(--mf-text)", cursor: loadingMore ? "wait" : "pointer",
                    fontWeight: 700, fontSize: 13,
                  }}
                >
                  {loadingMore ? "Chargement…" : "Charger plus"}
                </button>
              </div>
            )}
            <div style={{ marginTop: 16, fontSize: 11, color: "var(--mf-text-muted)", textAlign: "center", fontStyle: "italic" }}>
              Les événements plus vieux que 90 jours sont automatiquement archivés.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function renderMeta(it) {
  const m = it.meta || {};
  if (it.kind === "mileage_updated" && typeof m.mileage === "number") {
    return `${m.mileage.toLocaleString("fr-FR")} km`;
  }
  if (typeof m.amount === "number") {
    return m.amount.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
  }
  return "";
}
